/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import React, { useState, useEffect, useMemo } from "react";
import { bem, BEM } from "../../shared/BEM";
import { ModelOutput } from "../../shared/model/ModelOutput";
import { IModelOutputState } from "../../shared/model/IModelOutputState";
import { Loading } from "../Loading";

/**
 * Common output field props
 */
export interface IOutputCommonProps<TModelOutput extends ModelOutput<any, any> = ModelOutput<any, any>> {
	/** Input label */
	label?: string;

	/** Input description */
	description?: string;

	/** Custom field modifiers */
	modifiers?: Array<string>;

	/** If to provide and update also on global model update */
	updateOnModelChange?: boolean;

	/** More info renderer */
	renderMore?: (state: ReturnType<TModelOutput["getState"]>, baseStyle: BEM, handler: ReturnType<TModelOutput["getHandler"]>)
		=> React.ReactElement;
}

/**
 * Component props
 */
export interface IOutputWrapperProps<TModelOutput extends ModelOutput<any>> extends IOutputCommonProps {
	/** Output field */
	output: TModelOutput;

	/** Value renderer */
	render: (state: ReturnType<TModelOutput["getState"]>, baseStyle: BEM) => React.ReactElement;
}

/**
 * Output field wrapper component
 *
 * @param props Props
 */
export function OutputWrapper<TModelOutput extends ModelOutput<any>>(props: IOutputWrapperProps<TModelOutput>) {

	// Get initial state
	const [ outputState, setOutputState ] = useState(() => props.output.getState());
	const [ _modelState, setModelState ] = useState(0);
	const [ opened, setOpened ] = useState(false);

	// Bind on change events
	useEffect(() => {

		const updateHandler = (event: IModelOutputState<any, any>) => {
			setOutputState(event);
		};

		const modelUpdateHandler = (_event: IModelOutputState<any, any>) => {
			setModelState((s) => s + 1);
		};

		props.output.on("update", updateHandler);
		updateHandler(props.output.getState());

		if (props.updateOnModelChange) {
			props.output.getHandler().on("update", modelUpdateHandler);
		}

		return () => {
			props.output.off("update", updateHandler);

			if (props.updateOnModelChange) {
				props.output.getHandler().off("update", modelUpdateHandler);
			}
		};

	}, [ props.output, props.updateOnModelChange ]);

	const handleToggleOpen = useMemo(() => () => {
		setOpened((state) => !state);
	}, []);

	// Get style
	const baseStyle = bem("field-output");

	const mods = props.modifiers ? props.modifiers.slice() : [];

	if (opened) {
		mods.push("opened");
	}

	if (props.renderMore) {
		mods.push("with-more");
	}

	let style = baseStyle.mod(mods);

	if (outputState.loading) {
		style = style.mod(["loading"], true);
	}

	if (outputState.error) {
		style = style.mod(["error"], true);
	}

	// Render
	return <div className={style.className()}>
		<div className={style.el("line").className()} onClick={handleToggleOpen}>
			{ props.renderMore ?
			<i className={`mdi oc mdi-${ opened ? "chevron-down" : "chevron-right" }`} />
			: null }
			<dt className={baseStyle.el("label").className()}>{props.label || outputState.config.label}</dt>
			<dd className={baseStyle.el("field").className()}>
				<Loading size="xs" />
				<i className="mdi mdi-alert" title="Chyba" />
				{props.render((outputState as any) as ReturnType<TModelOutput["getState"]>, baseStyle)}
			</dd>
		</div>
		{ props.renderMore ?
		<div className={style.el("more").className()}>
			{props.renderMore((outputState as any) as ReturnType<TModelOutput["getState"]>, baseStyle, props.output.getHandler())}
		</div>
		: null }
	</div>;

}
